'use client';

/**
 * Default debug-feed renderer: a scrolling log tail of AgentDebugEvents with a
 * collapsible JSON tree per event, filtering, pause/resume, copy, and
 * "expand full" for truncated payloads. Binary blobs render inline via
 * <MediaBlob>. See docs/AGENT_DEBUG_FEEDS_DESIGN.md §5.5.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, Copy, Pause, Play, ArrowDownToLine } from 'lucide-react';
import type { AgentDebugEvent, MediaBlob as MediaBlobT } from '@/lib/debug/types';
import { isMediaBlob, isTruncatedField, payloadHasTruncation } from '@/lib/debug/types';
import { NativeSelectAdapter } from '@/components/ui/native-select-adapter';
import { MediaBlob } from './MediaBlob';

export interface FeedRendererProps {
  events: AgentDebugEvent[];
  callId: string | null;
  paused?: boolean;
}

function fmtTime(ts: number): string {
  const d = new Date(ts);
  const p2 = (n: number) => String(n).padStart(2, '0');
  return `${p2(d.getHours())}:${p2(d.getMinutes())}:${p2(d.getSeconds())}.${String(d.getMilliseconds()).padStart(3, '0')}`;
}

const LEVEL_BADGE: Record<string, string> = {
  debug: 'bg-slate-700 text-slate-300',
  info: 'bg-sky-900/60 text-sky-300',
  warn: 'bg-amber-900/60 text-amber-300',
  error: 'bg-red-900/60 text-red-300',
};

// ── tiny JSON tree ──────────────────────────────────────────────────────────
function JsonNode({
  k,
  v,
  depth,
  eventId,
  callId,
}: {
  k?: string;
  v: unknown;
  depth: number;
  eventId: string;
  callId: string | null;
}) {
  const [open, setOpen] = useState(depth < 2);
  const indent = { paddingLeft: `${depth * 12}px` };

  if (isMediaBlob(v)) {
    return (
      <div style={indent} className="py-0.5">
        {k != null && <span className="text-violet-300">{k}: </span>}
        <MediaBlob blob={v as MediaBlobT} eventId={eventId} callId={callId} />
      </div>
    );
  }
  if (isTruncatedField(v)) {
    const t = v as { _len: number; _preview?: string };
    return (
      <div style={indent} className="py-0.5">
        {k != null && <span className="text-violet-300">{k}: </span>}
        <span className="whitespace-pre-wrap break-words text-emerald-300">{t._preview ?? ''}</span>
        <span className="ml-1 rounded bg-amber-900/50 px-1 text-[10px] text-amber-300">
          +{t._len - (t._preview?.length ?? 0)} more chars — truncated
        </span>
      </div>
    );
  }
  if (v === null || typeof v !== 'object') {
    const isStr = typeof v === 'string';
    return (
      <div style={indent} className="py-0.5">
        {k != null && <span className="text-violet-300">{k}: </span>}
        <span className={isStr ? 'whitespace-pre-wrap break-words text-emerald-300' : 'text-cyan-300'}>
          {isStr ? (v as string) : String(v)}
        </span>
      </div>
    );
  }
  const entries = Array.isArray(v) ? (v as unknown[]).map((x, i) => [String(i), x] as const) : Object.entries(v as Record<string, unknown>);
  const label = Array.isArray(v) ? `[${entries.length}]` : `{${entries.length}}`;
  return (
    <div style={indent} className="py-0.5">
      <button onClick={() => setOpen((o) => !o)} className="inline-flex items-center gap-0.5 text-slate-400 hover:text-slate-200">
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        {k != null && <span className="text-violet-300">{k}</span>}
        <span className="text-slate-500">{label}</span>
      </button>
      {open && entries.map(([ck, cv]) => <JsonNode key={ck} k={ck} v={cv} depth={depth + 1} eventId={eventId} callId={callId} />)}
    </div>
  );
}

function EventRow({ ev, callId }: { ev: AgentDebugEvent; callId: string | null }) {
  const [expanded, setExpanded] = useState(false);
  const [fullPayload, setFullPayload] = useState<unknown>(undefined);
  const [loadingFull, setLoadingFull] = useState(false);
  const truncated = useMemo(() => payloadHasTruncation(ev.payload), [ev.payload]);

  const copy = useCallback(() => {
    try {
      navigator.clipboard?.writeText(JSON.stringify(ev, null, 2));
    } catch {
      /* ignore */
    }
  }, [ev]);

  const loadFull = useCallback(async () => {
    if (!callId || loadingFull) return;
    setLoadingFull(true);
    try {
      const res = await fetch(`/api/debug/agent-event/${encodeURIComponent(ev.id)}?callId=${encodeURIComponent(callId)}`);
      if (res.ok) {
        const fullEv = await res.json();
        setFullPayload(fullEv?.payload);
      }
    } catch {
      /* ignore */
    } finally {
      setLoadingFull(false);
    }
  }, [callId, ev.id, loadingFull]);

  const payloadToShow = fullPayload !== undefined ? fullPayload : ev.payload;

  return (
    <div className="border-b border-white/[0.06] px-2 py-1 font-mono text-[11px] leading-relaxed">
      <div className="flex items-center gap-2">
        <button onClick={() => setExpanded((e) => !e)} className="text-slate-500 hover:text-slate-300">
          {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        </button>
        <span className="text-slate-500">{fmtTime(ev.ts)}</span>
        <span className="text-slate-400">{ev.source}</span>
        <span className="text-sky-300">{ev.kind}</span>
        {ev.turnId && <span className="rounded bg-indigo-900/50 px-1 text-[10px] text-indigo-300">{ev.turnId}</span>}
        {ev.level && ev.level !== 'info' && (
          <span className={`rounded px-1 text-[10px] ${LEVEL_BADGE[ev.level] ?? LEVEL_BADGE.info}`}>{ev.level}</span>
        )}
        <span className="ml-auto flex items-center gap-1">
          {truncated && fullPayload === undefined && (
            <button onClick={loadFull} className="text-blue-400 hover:text-blue-300 disabled:opacity-50" disabled={loadingFull}>
              {loadingFull ? 'loading…' : 'expand full'}
            </button>
          )}
          <button onClick={copy} className="text-slate-500 hover:text-slate-300" title="Copy event JSON">
            <Copy className="h-3 w-3" />
          </button>
        </span>
      </div>
      {expanded && (
        <div className="mt-1 max-h-[40vh] overflow-auto rounded bg-black/30 p-1">
          <JsonNode v={payloadToShow} depth={0} eventId={ev.id} callId={callId} />
        </div>
      )}
    </div>
  );
}

export function JsonLogRenderer({ events, callId, paused: pausedProp }: FeedRendererProps) {
  const [filter, setFilter] = useState('');
  const [kindFilter, setKindFilter] = useState<string>('');
  const [levelFilter, setLevelFilter] = useState<string>('');
  const [pausedLocal, setPausedLocal] = useState(false);
  const paused = pausedProp || pausedLocal;
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const stickToBottomRef = useRef(true);

  // Freeze the displayed events while paused.
  const frozenRef = useRef<AgentDebugEvent[]>(events);
  if (!paused) frozenRef.current = events;
  const source = paused ? frozenRef.current : events;

  const kinds = useMemo(() => Array.from(new Set(source.map((e) => e.kind))).sort(), [source]);

  const filtered = useMemo(() => {
    const f = filter.trim().toLowerCase();
    return source.filter((e) => {
      if (kindFilter && e.kind !== kindFilter) return false;
      if (levelFilter && (e.level ?? 'info') !== levelFilter) return false;
      if (f) {
        try {
          if (!JSON.stringify(e).toLowerCase().includes(f)) return false;
        } catch {
          return false;
        }
      }
      return true;
    });
  }, [source, filter, kindFilter, levelFilter]);

  useEffect(() => {
    if (paused || !stickToBottomRef.current) return;
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [filtered.length, paused]);

  const onScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
  }, []);

  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
      stickToBottomRef.current = true;
    }
  }, []);

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-wrap items-center gap-2 border-b border-white/[0.12] bg-[#0d0d0d] px-2 py-1.5 text-[11px]">
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          aria-label="Filter log entries"
          placeholder="filter…"
          className="w-40 rounded border border-white/[0.12] bg-[#0a0a0a] px-2 py-0.5 text-slate-200 placeholder:text-slate-600"
        />
        <NativeSelectAdapter
          aria-label="Filter by kind"
          size="sm"
          value={kindFilter}
          onChange={(e) => setKindFilter(e.target.value)}
          className="w-36"
        >
          <option value="">all kinds</option>
          {kinds.map((k) => (
            <option key={k} value={k}>
              {k}
            </option>
          ))}
        </NativeSelectAdapter>
        <NativeSelectAdapter
          aria-label="Filter by level"
          size="sm"
          value={levelFilter}
          onChange={(e) => setLevelFilter(e.target.value)}
          className="w-32"
        >
          <option value="">all levels</option>
          <option value="debug">debug</option>
          <option value="info">info</option>
          <option value="warn">warn</option>
          <option value="error">error</option>
        </NativeSelectAdapter>
        <button
          onClick={() => setPausedLocal((p) => !p)}
          className="inline-flex items-center gap-1 rounded border border-white/[0.12] px-1.5 py-0.5 text-slate-300 hover:bg-white/[0.06]"
        >
          {pausedLocal ? <Play className="h-3 w-3" /> : <Pause className="h-3 w-3" />}
          {pausedLocal ? 'resume' : 'pause'}
        </button>
        <button
          onClick={scrollToBottom}
          className="inline-flex items-center gap-1 rounded border border-white/[0.12] px-1.5 py-0.5 text-slate-300 hover:bg-white/[0.06]"
        >
          <ArrowDownToLine className="h-3 w-3" />
          bottom
        </button>
        <span className="ml-auto text-slate-500">
          {filtered.length}/{source.length}
        </span>
      </div>
      <div ref={scrollRef} onScroll={onScroll} className="flex-1 overflow-auto bg-[#0a0a0a]">
        {filtered.length === 0 ? (
          <div className="p-3 text-[11px] text-slate-600">no events</div>
        ) : (
          filtered.map((ev) => <EventRow key={ev.id} ev={ev} callId={callId} />)
        )}
      </div>
    </div>
  );
}

export default JsonLogRenderer;
